import { randomUUID } from "node:crypto";
import type { AgentTimelineItem } from "./agent-sdk-types.js";
import type {
  AgentTimelineFetchOptions,
  AgentTimelineFetchResult,
  AgentTimelineRow,
} from "./agent-timeline-store-types.js";

export type SeedAgentTimelineOptions = {
  items?: readonly AgentTimelineItem[];
  rows?: readonly AgentTimelineRow[];
  epoch?: string;
  nextSeq?: number;
  timestamp?: string;
};

type AgentTimelineState = {
  epoch: string;
  rows: AgentTimelineRow[];
  nextSeq: number;
};

const DEFAULT_TIMELINE_FETCH_LIMIT = 200;

function cloneRow(row: AgentTimelineRow): AgentTimelineRow {
  return { ...row };
}

function normalizeTimelineMessageId(messageId: string | undefined): string | undefined {
  if (typeof messageId !== "string") {
    return undefined;
  }
  const normalized = messageId.trim();
  return normalized.length > 0 ? normalized : undefined;
}

export class InMemoryAgentTimelineStore {
  private readonly states = new Map<string, AgentTimelineState>();

  has(agentId: string): boolean {
    return this.states.has(agentId);
  }

  initialize(agentId: string, options?: SeedAgentTimelineOptions): void {
    const timestamp = options?.timestamp ?? new Date().toISOString();
    const rows = options?.rows?.length
      ? options.rows.map(cloneRow)
      : this.buildRowsFromItems(options?.items ?? [], options?.nextSeq ?? 1, timestamp);
    const nextSeq = options?.nextSeq ?? (rows.length ? rows[rows.length - 1]!.seq + 1 : 1);
    this.states.set(agentId, {
      epoch: options?.epoch ?? randomUUID(),
      rows,
      nextSeq,
    });
  }

  delete(agentId: string): void {
    this.states.delete(agentId);
  }

  /**
   * Drop the row matching the given user-message id and every row after it,
   * then mint a fresh epoch so any client cursors against the old timeline
   * are treated as stale (forces a full re-fetch). Used by `rewindAgent` —
   * the conversation continues in the same agent, but the tail past the
   * rewind point is gone.
   *
   * Accepts a single id or an array of candidate ids. Rewind callers pass
   * both the client-supplied id (often an Appostle-internal `msg_<ts>_<rand>`
   * optimistic id) AND the resolved Claude transcript UUID, because the same
   * logical message can be stored under either id depending on whether the
   * row was minted locally via `recordUserMessage` or rehydrated from JSONL.
   *
   * Returns the number of rows that were dropped (0 when no match).
   */
  truncateAfterUserMessage(agentId: string, userMessageId: string | readonly string[]): number {
    const state = this.requireState(agentId);
    const candidateIds = (Array.isArray(userMessageId) ? userMessageId : [userMessageId])
      .map((id) => normalizeTimelineMessageId(id))
      .filter((id): id is string => Boolean(id));
    if (candidateIds.length === 0) {
      return 0;
    }
    const targetSet = new Set(candidateIds);
    const cutIndex = state.rows.findIndex((row) => {
      if (row.item.type !== "user_message") {
        return false;
      }
      const rowMessageId = normalizeTimelineMessageId(row.item.messageId);
      return rowMessageId !== undefined && targetSet.has(rowMessageId);
    });
    if (cutIndex < 0) {
      return 0;
    }
    const dropped = state.rows.length - cutIndex;
    state.rows = state.rows.slice(0, cutIndex);
    state.epoch = randomUUID();
    state.nextSeq = state.rows.length ? state.rows[state.rows.length - 1]!.seq + 1 : 1;
    return dropped;
  }

  getItems(agentId: string): AgentTimelineItem[] {
    return this.requireState(agentId).rows.map((row) => row.item);
  }

  getRows(agentId: string): AgentTimelineRow[] {
    return this.requireState(agentId).rows.map(cloneRow);
  }

  getEpoch(agentId: string): string {
    return this.requireState(agentId).epoch;
  }

  fetch(agentId: string, options?: AgentTimelineFetchOptions): AgentTimelineFetchResult {
    const state = this.requireState(agentId);
    const direction = options?.direction ?? "tail";
    const requestedLimit = options?.limit;
    const limit =
      requestedLimit === undefined
        ? DEFAULT_TIMELINE_FETCH_LIMIT
        : Math.max(0, Math.floor(requestedLimit));
    const cursor = options?.cursor;
    const minSeq = state.rows.length ? state.rows[0]!.seq : 0;
    const maxSeq = state.rows.length ? state.rows[state.rows.length - 1]!.seq : 0;
    const selectAll = limit === 0;

    const window = {
      minSeq,
      maxSeq,
      nextSeq: state.nextSeq,
    };

    if (cursor && typeof cursor.epoch === "string" && cursor.epoch !== state.epoch) {
      return {
        epoch: state.epoch,
        direction,
        reset: true,
        staleCursor: true,
        gap: false,
        window,
        hasOlder: false,
        hasNewer: false,
        rows: state.rows.map(cloneRow),
      };
    }

    const cloneRows = (items: AgentTimelineRow[]) => items.map(cloneRow);

    if (direction === "after" && cursor && state.rows.length > 0 && cursor.seq < minSeq - 1) {
      return {
        epoch: state.epoch,
        direction,
        reset: true,
        staleCursor: false,
        gap: true,
        window,
        hasOlder: false,
        hasNewer: false,
        rows: cloneRows(state.rows),
      };
    }

    if (state.rows.length === 0) {
      return {
        epoch: state.epoch,
        direction,
        reset: false,
        staleCursor: false,
        gap: false,
        window,
        hasOlder: false,
        hasNewer: false,
        rows: [],
      };
    }

    if (direction === "tail") {
      const selected =
        selectAll || limit >= state.rows.length
          ? state.rows
          : state.rows.slice(state.rows.length - limit);
      return {
        epoch: state.epoch,
        direction,
        reset: false,
        staleCursor: false,
        gap: false,
        window,
        hasOlder: selected.length > 0 && selected[0]!.seq > minSeq,
        hasNewer: false,
        rows: cloneRows(selected),
      };
    }

    if (direction === "after") {
      const baseSeq = cursor?.seq ?? 0;
      const startIdx = state.rows.findIndex((row) => row.seq > baseSeq);
      if (startIdx < 0) {
        return {
          epoch: state.epoch,
          direction,
          reset: false,
          staleCursor: false,
          gap: false,
          window,
          hasOlder: baseSeq >= minSeq,
          hasNewer: false,
          rows: [],
        };
      }

      const selected = selectAll
        ? state.rows.slice(startIdx)
        : state.rows.slice(startIdx, startIdx + limit);
      const lastSelected = selected[selected.length - 1];
      return {
        epoch: state.epoch,
        direction,
        reset: false,
        staleCursor: false,
        gap: false,
        window,
        hasOlder: selected[0]!.seq > minSeq,
        hasNewer: Boolean(lastSelected && lastSelected.seq < maxSeq),
        rows: cloneRows(selected),
      };
    }

    const beforeSeq = cursor?.seq ?? state.nextSeq;
    const endExclusive = state.rows.findIndex((row) => row.seq >= beforeSeq);
    const boundedRows = endExclusive < 0 ? state.rows : state.rows.slice(0, endExclusive);
    const selected =
      selectAll || limit >= boundedRows.length
        ? boundedRows
        : boundedRows.slice(boundedRows.length - limit);
    return {
      epoch: state.epoch,
      direction,
      reset: false,
      staleCursor: false,
      gap: false,
      window,
      hasOlder: selected.length > 0 && selected[0]!.seq > minSeq,
      hasNewer: endExclusive >= 0,
      rows: cloneRows(selected),
    };
  }

  append(
    agentId: string,
    item: AgentTimelineItem,
    options?: { timestamp?: string },
  ): AgentTimelineRow {
    const state = this.requireState(agentId);
    const row: AgentTimelineRow = {
      seq: state.nextSeq,
      timestamp: options?.timestamp ?? new Date().toISOString(),
      item,
    };
    state.nextSeq += 1;
    state.rows.push(row);
    return cloneRow(row);
  }

  getLastItem(agentId: string): AgentTimelineItem | null {
    const state = this.requireState(agentId);
    return state.rows[state.rows.length - 1]?.item ?? null;
  }

  getLastAssistantMessage(agentId: string): string | null {
    const rows = this.requireState(agentId).rows;
    const chunks: string[] = [];
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const item = rows[i]!.item;
      if (item.type !== "assistant_message") {
        if (chunks.length > 0) {
          break;
        }
        continue;
      }
      chunks.push(item.text);
    }

    if (chunks.length === 0) {
      return null;
    }

    return chunks.reverse().join("");
  }

  getCanonicalUserMessagesById(agentId: string): Map<string, string> {
    const entries = this.requireState(agentId).rows.flatMap<[string, string]>((row) => {
      if (row.item.type !== "user_message") {
        return [];
      }
      const messageId = normalizeTimelineMessageId(row.item.messageId);
      if (!messageId) {
        return [];
      }
      return [[messageId, row.item.text]];
    });
    return new Map(entries);
  }

  hasCommittedUserMessage(agentId: string, options: { messageId: string; text: string }): boolean {
    const messageId = normalizeTimelineMessageId(options.messageId);
    if (!messageId) {
      return false;
    }

    return this.requireState(agentId).rows.some((row) => {
      if (row.item.type !== "user_message") {
        return false;
      }
      const rowMessageId = normalizeTimelineMessageId(row.item.messageId);
      return rowMessageId === messageId && row.item.text === options.text;
    });
  }

  /**
   * Find the Claude transcript UUID previously stamped onto the user-message
   * row identified by `messageId` (the daemon-/client-issued optimistic id).
   * Returns undefined if no row carries that id, or if it hasn't been
   * upgraded yet. Fork/rewind use this to resolve the optimistic id to a
   * real UUID without falling through to the index-based JSONL walk.
   */
  getClaudeUuidForUserMessage(agentId: string, messageId: string): string | undefined {
    const targetMessageId = normalizeTimelineMessageId(messageId);
    if (!targetMessageId) {
      return undefined;
    }
    const state = this.states.get(agentId);
    if (!state) {
      return undefined;
    }
    for (const row of state.rows) {
      if (row.item.type !== "user_message") {
        continue;
      }
      const rowMessageId = normalizeTimelineMessageId(row.item.messageId);
      if (rowMessageId !== targetMessageId) {
        continue;
      }
      const claudeUuid = normalizeTimelineMessageId(row.item.claudeUuid);
      return claudeUuid;
    }
    return undefined;
  }

  /**
   * Stamp a Claude transcript UUID onto an existing optimistic user-message
   * row. Used when the Claude SDK echoes a user turn back from JSONL — instead
   * of appending a duplicate row, we upgrade the row that was minted locally
   * via `recordUserMessage`.
   *
   * Matches the most recent user-message row whose `text` equals `options.text`
   * and which doesn't yet carry a `claudeUuid`. Returns the upgraded row's
   * `messageId` on success (or empty string if the row had none), or undefined
   * if no candidate row was found.
   *
   * Idempotent for the same UUID — if the matching row already carries this
   * exact `claudeUuid` we treat it as a successful upgrade and return its
   * `messageId` without re-mutating.
   */
  upgradeUserMessageWithClaudeUuid(
    agentId: string,
    options: { text: string; claudeUuid: string },
  ): string | undefined {
    const claudeUuid = normalizeTimelineMessageId(options.claudeUuid);
    if (!claudeUuid) {
      return undefined;
    }
    const state = this.states.get(agentId);
    if (!state) {
      return undefined;
    }
    // Walk from newest to oldest — the SDK echo we're upgrading is for the
    // most recent turn the user sent, not an older one with identical text.
    for (let i = state.rows.length - 1; i >= 0; i -= 1) {
      const row = state.rows[i];
      if (!row || row.item.type !== "user_message") {
        continue;
      }
      if (row.item.text !== options.text) {
        continue;
      }
      const existingClaudeUuid = normalizeTimelineMessageId(row.item.claudeUuid);
      if (existingClaudeUuid === claudeUuid) {
        return normalizeTimelineMessageId(row.item.messageId) ?? "";
      }
      if (existingClaudeUuid !== undefined) {
        // Row already carries a different UUID — not a candidate (would mean
        // two distinct turns happened to share text; the older one is settled).
        continue;
      }
      row.item = { ...row.item, claudeUuid };
      return normalizeTimelineMessageId(row.item.messageId) ?? "";
    }
    return undefined;
  }

  private requireState(agentId: string): AgentTimelineState {
    const state = this.states.get(agentId);
    if (!state) {
      throw new Error(`Unknown agent '${agentId}'`);
    }
    return state;
  }

  private buildRowsFromItems(
    items: readonly AgentTimelineItem[],
    startSeq: number,
    timestamp: string,
  ): AgentTimelineRow[] {
    let nextSeq = startSeq;
    return items.map((item) => {
      const row: AgentTimelineRow = {
        seq: nextSeq,
        timestamp,
        item,
      };
      nextSeq += 1;
      return row;
    });
  }
}
